
import { getContentTypes } from './getServerDataContext';
import { spPageContextFacet, groupStringFacet } from './Facets';
import { processResponse } from './processResponse';
import { getServerData } from './getServerData';
import { ISPGetItemContext } from './interfaces/IGetServerData';
import { IOperationHandler } from '@ms/items-view/lib/private/store/actions/implementOperation';
import { getItemsOperation } from '@ms/items-view/lib/private/operations/getItems';
import {
  createSingleItemOperationHandler,
  ISingleOperationPayload,
  IOperationHandlerThunkAction
} from '@ms/items-view/lib/private/addons/registerOperationHandler';
import { IItemCacheDispatch, IItemCacheThunkAction } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { childItemsFacet } from '@ms/items-view/lib/private/facets/SetFacets';
import { readItem } from '@ms/items-view/lib/private/operations/readItem';
import { spItemGroupKeyFacet, spPageContextKeyFacet } from './Keys';

export const spItemGroupGetItemsOperationHandler: IOperationHandler<typeof spItemGroupKeyFacet.type, typeof getItemsOperation.type> =
  createSingleItemOperationHandler({
    maxParallelOperationCount: 8
  })(getSPItemGroup);

function getSPItemGroup(
  payload: ISingleOperationPayload<typeof spItemGroupKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    itemKey
  } = payload;

  const groupKey = spItemGroupKeyFacet.deserialize(itemKey);

  return async (dispatch: IItemCacheDispatch) => {
    const getGroupDataContext = dispatch(constructGetGroupContext(groupKey));
    const serverData = await dispatch(getServerData(getGroupDataContext));
    const contentTypes = dispatch(getContentTypes({ itemSetKey: groupKey.itemSetKey }));
    const {
      itemResolver,
      childKeys
    } = dispatch(processResponse({
      serverData,
      itemSetKey: groupKey.itemSetKey,
      currentPageContext: getGroupDataContext.currentPageContext,
      contentTypes,
      parentGroupKey: itemKey,
      isItemSetRequest: true
    }));

    itemResolver.resolveItems({
      items: {
        [itemKey]: {
          ...(childKeys.length > 0 ? childItemsFacet.pack({
            itemKeys: childKeys
          }) : {})
        }
      }
    });

    dispatch(itemResolver.execute());
  };
}

function constructGetGroupContext(
  groupKey: typeof spItemGroupKeyFacet.type
): IItemCacheThunkAction<ISPGetItemContext> {
  return (dispatch: IItemCacheDispatch): ISPGetItemContext => {
    const itemSetKey = groupKey.itemSetKey;

    let viewId = '';
    if (itemSetKey.viewKey) {
      viewId = itemSetKey.viewKey.viewId;
    } else if (groupKey.viewKey) {
      viewId = groupKey.viewKey.viewId;
    }

    const parentItemKey = itemSetKey;

    const spPageContextFacets = dispatch(readItem({
      pageContext: spPageContextFacet
    }, spPageContextKeyFacet.serialize({
      webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
      listFullUrl: parentItemKey.listFullUrl
    })));

    const groupFacets = dispatch(readItem({
      groupString: groupStringFacet
    }, spItemGroupKeyFacet.serialize(groupKey)));

    return {
      webAbsoluteUrl: parentItemKey.webAbsoluteUrl,
      listFullUrl: parentItemKey.listFullUrl,
      currentPageContext: spPageContextFacets && spPageContextFacets.pageContext,
      qosName: 'getSPItemGroup',
      rootFolder: parentItemKey.rootFolder,
      groupString: groupFacets && groupFacets.groupString,
      viewId: viewId,
      filterParams: itemSetKey && itemSetKey.filterParams,
      sortField: itemSetKey && itemSetKey.sortFieldKey,
      sortDir: (itemSetKey && itemSetKey.sortAscending === undefined) ? undefined :
        (itemSetKey && itemSetKey.sortAscending ? 'Asc' : 'Desc'),
      postDataContext: {
        needsSchema: true
      }
    };
  };
}
